import {useCallback, useEffect, useMemo, useState} from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, Trash2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ActionModuleKind,
  RewardApprovalBehavior,
  RewardType,
  type ActionModuleResponse,
  type BaseRewardCalculationTypeModel,
  type CreateActionModuleRequest,
} from '@/api/model'
import { LocalizationEntityType } from '@/api/model/localizationEntityType'
import { useEntityTranslations } from '@/components/common/translations/useEntityTranslations'
import { useNestedTranslations } from '@/components/common/translations/useNestedTranslations'
import { useActionModuleMutations } from '../hooks/useActionModuleMutations'
import { Button } from '@/components/shadcn/button'
import { DropdownMenuItem } from '@/components/shadcn/dropdown-menu'
import { SplitButton } from '@/components/common/SplitButton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { CopyDialog } from '../components/CopyDialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { notify } from '@/lib/notify'
import { useAuthStore } from '@/store/auth.store'
import CommonPropertiesTab from './common-tab/CommonPropertiesTab'
import SubtypesTab from './subtypes-tab/SubtypesTab'
import SchedulingStrategyTab from './scheduling-tab/SchedulingStrategyTab'
import RewardCalculationTab from './reward-tab/RewardCalculationTab'
import IncentivationSourceTab from './incentivation-tab/IncentivationSourceTab'
import AssignmentTab from './assignment-tab/AssignmentTab'
import type {
  ActionModuleEditorState,
  EditorActionModule,
  SubtypeState,
  SubtypeTranslations,
} from '@/lib/action-modules/registry/types'
import { DefaultUnknownSubtypeDefinition, incentivationSources, subtypeDefaultProperties, subtypes as subtypeRegistry } from '@/lib/action-modules/registry'
import { PropertyTypes, type PropertyType } from '@/lib/action-modules/registry/subtypes/types'
import {
  getApiCompaniesCurrentTranslationsEntityTypeEntityIdProperty,
  usePatchApiCompaniesCurrentActionmodulesActionModuleIdIcon
} from "@/api/generated/company-management-user/company-management-user.ts";
import {triggerAutoTranslation, useResolvedLanguages} from "@/components/common/translations/useAutoTranslate.ts";
import type {PlannedLanguages} from "@/components/common/translations/types.ts";
import {AutoTranslateAllButton} from "@/components/common/translations/AutoTranslateAllButton.tsx";
import {checklistItemTextProperty, subtypePropertyName} from "@/pages/action-modules/editor/subtypeTranslations.ts";
import {isEditorChecklistAnswerItemArray} from "@/lib/action-modules/registry/subtypes/checklist.ts";

// Property names must match the backend translation rows exactly (see
// ActionModuleEntityToResponseLogic) — camelCase, stable across the component's lifetime.
const TRANSLATABLE_PROPERTIES = [
  'name',
  'description',
  'employeeDescription',
  'completionDescriptionText',
  'positiveAnswerText',
  'negativeAnswerText',
]

interface Props {
  isAdmin: boolean
  /** Required in admin scope; absent in CMU scope. */
  companyId?: number
  actionModuleId?: number
  actionModule?: ActionModuleResponse
  isLoading: boolean
  onSaved: () => void
  onDeleted: () => void
  /** Open the freshly created copy (the page owns navigation). */
  onCopied: (copy: ActionModuleResponse) => void
}

function createInitialState(companyId?: number): ActionModuleEditorState {
  return {
    subtypes: { subtypes: [], selectedSubtype: undefined },
    schedulingStrategy: { selectedSchedulingStrategyType: null, schedulingStrategy: null },
    // Only one source exists today; pre-select it so a fresh module is configurable out of the box.
    incentivationSource: {
      selectedIncentivationSourceType: incentivationSources[0].type,
      incentivationSource: { ...incentivationSources[0].defaultValue },
    },
    rewardCalculation: { maxReward: 500, rewardType: RewardType.Points, rewardCalculationType: null, selectedRewardCalculationType: null, walletTypeId: null },
    actionModule: {
      id: 0,
      name: '',
      description: '',
      employeeDescription: '',
      completionDescriptionText: '',
      positiveAnswerText: '',
      negativeAnswerText: '',
      maxReward: 0,
      rewardType: RewardType.Nothing,
      rewardApprovalBehavior: RewardApprovalBehavior.BySystem,
      transactionTimestampDelay: null,
      transactionTimestampDayOfMonth: null,
      iconName: '',
      colorHex: '',
      iconUrl: null,
      maxUserCanComplete: null,
      canCompleteMultipleTimes: false,
      detailedEmailNotification: false,
      summaryEmailNotification: false,
      emailNotificationAddresses: [],
      isCompanyWideAssigned: false,
      assignedEmployeeUserIds: [],
      assignedGroupIds: [],
      order: 0,
      companyId: companyId ?? 0,
      kind: ActionModuleKind.Live,
    },
    isNew: true,
  }
}

export default function ActionModuleEditor(props: Props) {
  const { t } = useTranslation(['action-modules-wizard', 'common', 'action-modules'])
  const queryClient = useQueryClient()
  // Only Super Admins (role === 'Administrator') may create/assign the Global Template kind.
  // Company Management Users and Company Admins are restricted to Company Templates (and Live).
  const role = useAuthStore(s => s.role)
  const isSuperAdmin = role === 'Administrator'
  const [tab, setTab] = useState('general')
  const [editorState, setEditorState] = useState<ActionModuleEditorState>(() => createInitialState(props.companyId))
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmCopy, setConfirmCopy] = useState(false)
  const [confirmSuperAdminCopy, setConfirmSuperAdminCopy] = useState(false)
  const [copyModule, setCopyModule] = useState(false)
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null)
  const patchIcon = usePatchApiCompaniesCurrentActionmodulesActionModuleIdIcon()
  // Make sure that only super admins can edit global templates.
  const isAllowedToEdit = editorState.actionModule.kind === ActionModuleKind.Live
    || editorState.actionModule.kind === ActionModuleKind.CompanyTemplate
    || (editorState.actionModule.kind === ActionModuleKind.GlobalTemplate && isSuperAdmin)
  const isTemplate = editorState.actionModule.kind === ActionModuleKind.CompanyTemplate
    || editorState.actionModule.kind === ActionModuleKind.GlobalTemplate

  const api = useActionModuleMutations({ isAdmin: props.isAdmin, companyId: props.companyId })
  const isSaving = api.isSaving

  const languages = useResolvedLanguages()
  const [autoTranslateAll, setAutoTranslateAll] = useState(false)
  const [plannedLanguages, setPlannedLanguages] = useState<Record<string, PlannedLanguages>>({})
  const getPlanned = (property: string): PlannedLanguages =>
    plannedLanguages[property] ?? new Set()

  const setPlanned = (property: string, planned: PlannedLanguages) =>
    setPlannedLanguages((prev) => ({ ...prev, [property]: planned }))
  // Buffered content translations — persisted only after the module was saved
  // (on create the Id exists only afterwards).
  const translations = useEntityTranslations({
    entityType: LocalizationEntityType.ActionModule,
    entityId: props.actionModuleId ? String(props.actionModuleId) : undefined,
    properties: TRANSLATABLE_PROPERTIES,
  })

  // Subtype-level texts: same buffered-until-save idea, but keyed by a property that carries the
  // subtype/checklist-item id — hence only offered for rows that were already persisted.
  const nestedTranslations = useNestedTranslations()
  const subtypeTranslations: SubtypeTranslations = useMemo(() => ({
    entityId: props.actionModuleId ? String(props.actionModuleId) : undefined,
    get: nestedTranslations.get,
    set: nestedTranslations.set,
    getPlanned: nestedTranslations.getPlanned,
    setPlanned: nestedTranslations.setPlanned,
    setServerLoaded: nestedTranslations.setServerLoaded,
  }), [props.actionModuleId, nestedTranslations.get, nestedTranslations.set, nestedTranslations.getPlanned, nestedTranslations.setPlanned, nestedTranslations.setServerLoaded])

  const buildSubtypeTranslationProperties = useCallback(<T,>(
    mapProperty: (property: string, sourceValue: string) => T
  ): T[] => {
    return editorState.subtypes.subtypes.flatMap((s) => {
      if (s.subtype.id == null) return []

      const regular = [...subtypeDefaultProperties, ...s.definition.editableProperties]
        .filter((p) => p.translatable)
        .map((p) => {
          const property = subtypePropertyName(s.subtype.id!, p.name)
          return mapProperty(property, s.properties[p.name] as string ?? '')
        })

      const checklistItems = s.properties['checklistItems']
      const checklist = isEditorChecklistAnswerItemArray(checklistItems)
        ? checklistItems
          .filter((item) => item.id != null)
          .map((item) => {
            const property = checklistItemTextProperty(s.subtype.id!, item.id!)
            return mapProperty(property, item.text ?? '')
          })
        : []

      return [...regular, ...checklist]
    })
  }, [editorState.subtypes.subtypes])

  // Hydrate editor state from a loaded module (edit mode).
  useEffect(() => {
    const actionModule = props.actionModule
    if (!actionModule) return

    const subtypeStates: SubtypeState[] = (actionModule.subtypes ?? []).map((s, i) => {
      const record = s as Record<string, unknown>
      const definition = subtypeRegistry.find(d => d.type === record.subtype) ?? DefaultUnknownSubtypeDefinition
      const properties: Record<string, PropertyType> = {}
      const propertyDefinitions = [...definition.editableProperties, ...subtypeDefaultProperties]

      for (const property of propertyDefinitions) {
        const value = record[property.name]
        if (property.propertyType === PropertyTypes.complex && property.complexModelToEditorState) {
          properties[property.name] = property.complexModelToEditorState(value)
        } else {
          properties[property.name] = (value ?? undefined) as PropertyType
        }
      }

      return {
        definition,
        subtype: { id: (record.id as number | null) ?? null },
        properties,
        index: i,
        editorId: i,
      }
    })

    setAutoTranslateAll(false)
    setPlannedLanguages({})

    setEditorState({
      rewardCalculation: {
        rewardCalculationType: (actionModule.rewardCalculationType as ActionModuleEditorState['rewardCalculation']['rewardCalculationType']) ?? null,
        selectedRewardCalculationType: actionModule.rewardCalculationType?.type ?? null,
        maxReward: actionModule.maxReward,
        rewardType: actionModule.rewardType,
        // Two of the generated calculation type models carry an index signature, which widens
        // `walletTypeId` on the union — read it off the shared base model instead.
        walletTypeId:
          (actionModule.rewardCalculationType as BaseRewardCalculationTypeModel | null)?.walletTypeId ?? null,
      },
      incentivationSource: {
        selectedIncentivationSourceType: actionModule.incentivationSource?.type ?? null,
        incentivationSource: (actionModule.incentivationSource as ActionModuleEditorState['incentivationSource']['incentivationSource']) ?? null,
      },
      schedulingStrategy: {
        selectedSchedulingStrategyType: actionModule.schedulingStrategy?.type ?? null,
        schedulingStrategy: (actionModule.schedulingStrategy as ActionModuleEditorState['schedulingStrategy']['schedulingStrategy']) ?? null,
      },
      subtypes: { subtypes: subtypeStates, selectedSubtype: undefined },
      actionModule: {
        id: actionModule.id ?? 0,
        name: actionModule.name ?? '',
        description: actionModule.description ?? '',
        employeeDescription: actionModule.employeeDescription ?? '',
        completionDescriptionText: actionModule.completionDescriptionText ?? '',
        positiveAnswerText: actionModule.positiveAnswerText ?? '',
        negativeAnswerText: actionModule.negativeAnswerText ?? '',
        maxReward: actionModule.maxReward,
        rewardType: actionModule.rewardType,
        rewardApprovalBehavior: actionModule.rewardApprovalBehavior ?? RewardApprovalBehavior.BySystem,
        transactionTimestampDelay: actionModule.transactionTimestampDelay ?? null,
        transactionTimestampDayOfMonth: actionModule.transactionTimestampDayOfMonth ?? null,
        iconName: actionModule.iconName ?? '',
        colorHex: actionModule.colorHex ?? '',
        iconUrl: actionModule.iconUrl ?? null,
        maxUserCanComplete: actionModule.maxUserCanComplete ?? null,
        canCompleteMultipleTimes: actionModule.canCompleteMultipleTimes ?? false,
        detailedEmailNotification: actionModule.detailedEmailNotification,
        summaryEmailNotification: actionModule.summaryEmailNotification,
        emailNotificationAddresses: actionModule.emailNotificationAddresses ?? [],
        isCompanyWideAssigned: actionModule.isCompanyWideAssigned,
        assignedEmployeeUserIds: actionModule.assignedEmployeeUserIds ?? [],
        assignedGroupIds: actionModule.assignedGroupIds ?? [],
        order: actionModule.order ?? 0,
        companyId: actionModule.companyId ?? props.companyId ?? 0,
        kind: actionModule.kind ?? ActionModuleKind.Live,
      },
      isNew: false,
    })
  }, [props.actionModule, props.companyId])

  useEffect(() => {
    const actionModule = props.actionModule
    if (!actionModule || !props.actionModuleId) return

    const entityId = String(props.actionModuleId)

    const propertiesToPreload: string[] = (actionModule.subtypes ?? []).flatMap((s) => {
      const record = s as Record<string, unknown>
      const subtypeId = record.id as number | null
      if (subtypeId == null) return []

      const definition = subtypeRegistry.find(d => d.type === record.subtype) ?? DefaultUnknownSubtypeDefinition
      const regular = [...subtypeDefaultProperties, ...definition.editableProperties]
        .filter((p) => p.translatable)
        .map((p) => subtypePropertyName(subtypeId, p.name))

      const checklistItems = record.checklistItems as { id: number }[] | undefined
      const checklist = Array.isArray(checklistItems)
        ? checklistItems
          .filter((item) => item.id != null)
          .map((item) => checklistItemTextProperty(subtypeId, item.id))
        : []

      return [...regular, ...checklist]
    })

    for (const property of propertiesToPreload) {
      getApiCompaniesCurrentTranslationsEntityTypeEntityIdProperty(
        LocalizationEntityType.ActionModule, entityId, property
      ).then((response) => {
        const loaded = response.data as Record<string, string> | undefined
        if (loaded) nestedTranslations.setServerLoaded(property, loaded)
      }).catch(() => {})
    }
  }, [props.actionModule, props.actionModuleId, nestedTranslations.setServerLoaded])


  const updateModule = (patch: Partial<EditorActionModule>) =>
    setEditorState(s => ({ ...s, actionModule: { ...s.actionModule, ...patch } }))

  const buildRequest = useCallback((): CreateActionModuleRequest => {
    const subtypeModels = editorState.subtypes.subtypes.map((s, i) => {
      const model: Record<string, unknown> = { ...s.subtype, id: s.subtype.id, order: i, subtype: s.definition.type }
      const properties = [...subtypeDefaultProperties, ...s.definition.editableProperties]
      for (const property of properties) {
        const editorStateValue = s.properties[property.name] ?? property.defaultValue
        const mapped =
          property.complexEditorStateToModel && editorStateValue
            ? property.complexEditorStateToModel(editorStateValue)
            : editorStateValue
        // Backend subtype models mark many properties `required` (the JSON key must be present).
        // The editor may leave an optional or defaultless property untouched → `undefined`, which
        // JSON.stringify drops, tripping the backend's "missing required property" validation.
        // Emit a type-appropriate fallback instead of omitting: `false` for booleans (those
        // required fields are non-nullable), `null` otherwise (the required ones are nullable).
        model[property.name] = mapped ?? (property.propertyType === PropertyTypes.boolean ? false : null)
      }
      return model
    })

    const schedulingStrategy = editorState.schedulingStrategy.selectedSchedulingStrategyType
      ? { ...editorState.schedulingStrategy.schedulingStrategy, type: editorState.schedulingStrategy.selectedSchedulingStrategyType }
      : null
    // The wallet type is stored on the calculation type row — without one there is nothing to
    // attach it to, and the backend ignores it.
    const rewardCalculationType = editorState.rewardCalculation.selectedRewardCalculationType
      ? {
        ...editorState.rewardCalculation.rewardCalculationType,
        type: editorState.rewardCalculation.selectedRewardCalculationType,
        walletTypeId: editorState.rewardCalculation.walletTypeId,
      }
      : null
    const incentivationSource = editorState.incentivationSource.selectedIncentivationSourceType
      ? { ...editorState.incentivationSource.incentivationSource, type: editorState.incentivationSource.selectedIncentivationSourceType }
      : null

    // id/iconUrl are not part of the request; rewardType/maxReward come from the reward tab.
    const { id: _id, iconUrl: _iconUrl, rewardType: _rt, maxReward: _mr, ...common } = editorState.actionModule
    return {
      ...common,
      // Drop blank rows the multi-email editor may leave behind, trim the rest.
      emailNotificationAddresses: common.emailNotificationAddresses
        .map(a => a.trim())
        .filter(a => a.length > 0),
      // Company-wide assignment ignores granular selection (mirrors the service-module editor).
      assignedGroupIds: common.isCompanyWideAssigned ? [] : common.assignedGroupIds,
      assignedEmployeeUserIds: common.isCompanyWideAssigned ? [] : common.assignedEmployeeUserIds,
      rewardType: editorState.rewardCalculation.rewardType,
      maxReward: editorState.rewardCalculation.maxReward,
      rewardCalculationType: rewardCalculationType as CreateActionModuleRequest['rewardCalculationType'],
      incentivationSource: incentivationSource as CreateActionModuleRequest['incentivationSource'],
      schedulingStrategy: schedulingStrategy as CreateActionModuleRequest['schedulingStrategy'],
      subtypes: subtypeModels as unknown as CreateActionModuleRequest['subtypes'],
    }
  }, [editorState])

  const onSave = useCallback(async () => {
    const request = buildRequest()
    try {
      // Create exists in both scopes (admin: administration endpoint, CMU: companies/current).
      if (editorState.isNew && !api.canCreate) return
      const saved = editorState.isNew
        ? editorState.actionModule.kind === ActionModuleKind.Live
          ? (await api.create(request)).data
          : isSuperAdmin ?
            (await api.createAdminTemplate(editorState.actionModule.kind === ActionModuleKind.GlobalTemplate, request, false)).data
            : (await api.createCompanyTemplate(request, false)).data
        : (await api.patch(editorState.actionModule.id, request)).data

      const savedId = saved.id ?? editorState.actionModule.id
      if (pendingIconFile) {
        await patchIcon.mutateAsync({ actionModuleId: savedId, data: { File: pendingIconFile } })
        setPendingIconFile(null)
      }
      // Persist translations only now — on create the module's Id doesn't exist until this point.
      // (Subtype translations already carry persisted ids: they can only be edited after a save.)
      await translations.save(String(savedId))
      await nestedTranslations.save(LocalizationEntityType.ActionModule, String(savedId))
      const allNestedPlanned = nestedTranslations.getAllPlanned()

      triggerAutoTranslation({
        entityType: 'ActionModule',
        entityId: String(savedId),
        properties: [
          ...TRANSLATABLE_PROPERTIES.map((property) => ({
            property,
            plannedLanguages: autoTranslateAll
              ? languages.map((l) => l.code)
              : [...getPlanned(property)],
            existingTranslations: translations.get(property),
          })),
          ...buildSubtypeTranslationProperties((property) => ({
            property,
            plannedLanguages: autoTranslateAll
              ? languages.map((l) => l.code)
              : [...(allNestedPlanned[property] ?? new Set())],
            existingTranslations: nestedTranslations.getExisting(property),
          })),
        ],
      })
      notify.success(editorState.isNew ? t('editor.createdSuccess') : t('editor.savedSuccess'))
      queryClient.invalidateQueries()
      props.onSaved()
    } catch (error) {
      notify.error(t('editor.saveError'), { traceId: (error as { response?: { headers?: Record<string, string> } })?.response?.headers?.['x-trace-id'] })
    }
  }, [buildRequest, editorState, api, props, queryClient, t, translations, nestedTranslations.getExisting, nestedTranslations.getAllPlanned, nestedTranslations.save, buildSubtypeTranslationProperties, autoTranslateAll, languages, getPlanned])

  const onCopy = useCallback(async (createGlobalTemplate = false) => {
    try {
      if (editorState.isNew && !api.canCreate) return
      if (props.actionModuleId === undefined) return
      const copied = props.actionModule?.kind === ActionModuleKind.Live
        ? isSuperAdmin && createGlobalTemplate // Super-admin opt-in: copy the live module into a Global Template via the admin
          // create-template endpoint (no dedicated "copy to global template" endpoint exists,
          // so we send the full request derived from the current editor state).
          ? (await api.createGlobalTemplateFromLiveActionModule(props.actionModuleId)).data
          : (await api.createCompanyTemplateFromLiveActionModule(props.actionModuleId)).data
        : (await api.createLiveActionModuleFromTemplate(props.actionModuleId)).data
      const savedId = copied.id ?? editorState.actionModule.id
      if (pendingIconFile) {
        await patchIcon.mutateAsync({ actionModuleId: savedId, data: { File: pendingIconFile } })
        setPendingIconFile(null)
      }
      // Persist translations only now — on create the module's Id doesn't exist until this point.
      // (Subtype translations already carry persisted ids: they can only be edited after a save.)
      await translations.save(String(savedId))
      await nestedTranslations.save(LocalizationEntityType.ActionModule, String(savedId))
      notify.success(copied.kind === ActionModuleKind.Live
        ? t('actionModuleTemplate.CopyFromTemplateSuccess', { ns: 'action-modules' })
        : t('actionModuleTemplate.CopyFromLiveSuccess', { ns: 'action-modules' }))
      queryClient.invalidateQueries()
      props.onSaved()
    } catch (error) {
      notify.error(t('editor.saveError'), { traceId: (error as { response?: { headers?: Record<string, string> } })?.response?.headers?.['x-trace-id'] })
    }
  }, [buildRequest, editorState, api, props, queryClient, t, translations, nestedTranslations.save])

  const onDelete = useCallback(async () => {
    if (!props.actionModuleId || !api.canDelete) return
    try {
      editorState.actionModule.kind === ActionModuleKind.Live
        ? await api.remove(props.actionModuleId)
        : await api.removeCompanyTemplate(props.actionModuleId)
      notify.success(t('editor.deletedSuccess'))
      queryClient.invalidateQueries()
      props.onDeleted()
    } catch (error) {
      notify.error(t('editor.deleteError'), { traceId: (error as { response?: { headers?: Record<string, string> } })?.response?.headers?.['x-trace-id'] })
    }
  }, [api, props, queryClient, t])

  return (
    <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex items-start gap-2">
        <TabsList className="h-auto! flex-wrap gap-1 [&_[data-slot=tabs-trigger]]:h-auto">
          <TabsTrigger value="general">{t('tabs.general')}</TabsTrigger>
          <TabsTrigger value="subtypes">{t('tabs.subtypes')}</TabsTrigger>
          <TabsTrigger value="scheduling">{t('tabs.scheduling')}</TabsTrigger>
          <TabsTrigger value="reward">{t('tabs.reward')}</TabsTrigger>
          <TabsTrigger value="incentivationSource">{t('tabs.incentivationSource')}</TabsTrigger>
          <TabsTrigger disabled={isTemplate} value="assignment">{t('tabs.assignment')}</TabsTrigger>
        </TabsList>
        <div className="ml-auto flex items-center gap-2">

          {/*Create a new template from an existing live action module*/}
          <Button disabled={isSaving || props.isLoading || isTemplate || editorState.isNew}
                  hidden={isTemplate}
                  onClick={() => (isSuperAdmin ? setConfirmSuperAdminCopy(true) : setConfirmCopy(true))}>
            {t('actionModuleTemplate.CopyFromLive')}
          </Button>
          {/*Create a new live action module from an existing template*/}
          <Button disabled={isSaving || props.isLoading || !isTemplate || editorState.isNew}
                  hidden={!isTemplate}
                  onClick={() => setConfirmCopy(true)}>
            {t('actionModuleTemplate.CopyFromTemplate')}
          </Button>

          <label>{t('actionModuleKind.label', { ns: 'action-modules' })}</label>
          <Select
            disabled={!editorState.isNew}
            value={editorState.actionModule.kind}
            onValueChange={v => {
              updateModule({ kind: v as ActionModuleKind })
              // Templates don't support the assignment tab — bounce back to general so the user
              // isn't stranded on a disabled tab after switching kind.
              if (tab === 'assignment') setTab('general')
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t('actionModuleKind.label', { ns: 'action-modules' })} />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ActionModuleKind)
                // Only allow global templates for super admins or when editing an existing module
                .filter(kind => isSuperAdmin || kind !== ActionModuleKind.GlobalTemplate || !!props.actionModuleId)
                .map(kind => (
                  <SelectItem key={kind} value={kind}>
                    {t(`actionModuleKind.options.${kind}`, { ns: 'action-modules' })}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {props.actionModuleId && api.canDelete && (
            <Button variant="destructive" size="icon" disabled={isSaving || !isAllowedToEdit} onClick={() => setConfirmDelete(true)}>
              <Trash2 className="size-4" />
            </Button>
          )}
          <AutoTranslateAllButton
            disabled={!isAllowedToEdit}
            properties={[
              ...TRANSLATABLE_PROPERTIES.map((property) => ({
                property,
                sourceValue: editorState.actionModule[property as keyof EditorActionModule] as string ?? '',
                existingTranslations: {},
                manualTranslations: translations.get(property),
              })),
              ...buildSubtypeTranslationProperties((property, sourceValue) => ({
                property,
                sourceValue,
                existingTranslations: nestedTranslations.getExisting(property),
                manualTranslations: nestedTranslations.get(property) ?? {},
              })),
            ]}
            autoTranslateAll={autoTranslateAll}
            onAutoTranslateAllChange={setAutoTranslateAll}
          />
          <SplitButton
            disabled={isSaving || props.isLoading || !isAllowedToEdit}
            onClick={onSave}
            menu={
              /* Copying needs a persisted module — the endpoint copies server-side state. */
              <DropdownMenuItem
                className="whitespace-nowrap"
                disabled={!props.actionModuleId || editorState.isNew}
                onClick={() => setCopyModule(true)}
              >
                <Copy className="size-4" />
                {t('copyDialog.title', { ns: 'action-modules' })}
              </DropdownMenuItem>
            }
          >
            {t('buttons.save', { ns: 'common' })}
          </SplitButton>
        </div>
      </div>

      {/* Flex column so the active TabsContent's flex-1 fills the bounded height (rather than
          relying on percentage h-full). overflow-hidden pins the editor: each tab scrolls
          internally — forms via their own overflow-auto, the subtypes tab per column. */}
      <div className="mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border bg-card">
        <TabsContent value="general" className="min-h-0 min-w-0 overflow-auto">
          <CommonPropertiesTab
            actionModule={editorState.actionModule}
            onChanged={updateModule}
            translations={translations}
            iconBusy={patchIcon.isPending}
            onUploadIcon={(file, dataUrl) => {
              setPendingIconFile(file)
              updateModule({ iconUrl: dataUrl })
            }}
            onDeleteIcon={() => {
              setPendingIconFile(null)
              updateModule({ iconUrl: null })
            }}
            disabled = {!isAllowedToEdit}
            autoTranslateAll={autoTranslateAll}
            getPlanned={getPlanned}
            setPlanned={setPlanned}
          />
        </TabsContent>
        <TabsContent value="subtypes" className="flex min-h-0 min-w-0 flex-col">
          <SubtypesTab
            value={editorState.subtypes}
            onChanged={v => setEditorState(s => ({ ...s, subtypes: v }))}
            translations={subtypeTranslations}
            disabled = {!isAllowedToEdit}
          />
        </TabsContent>
        <TabsContent value="scheduling" className="min-h-0 min-w-0 overflow-auto">
          <SchedulingStrategyTab
            value={editorState.schedulingStrategy}
            onChange={v => setEditorState(s => ({ ...s, schedulingStrategy: v }))}
            disabled = {!isAllowedToEdit}
          />
        </TabsContent>
        <TabsContent value="reward" className="min-h-0 min-w-0 overflow-auto">
          <RewardCalculationTab
            value={editorState.rewardCalculation}
            onChange={v => setEditorState(s => ({ ...s, rewardCalculation: v }))}
            actionModule={editorState.actionModule}
            onModuleChanged={updateModule}
            walletTypeCompanyId={props.isAdmin ? props.companyId : undefined}
            disabled = {!isAllowedToEdit}
          />
        </TabsContent>
        <TabsContent value="incentivationSource" className="min-h-0 min-w-0 overflow-auto">
          <IncentivationSourceTab
            value={editorState.incentivationSource}
            onChange={v => setEditorState(s => ({ ...s, incentivationSource: v }))}
            disabled = {!isAllowedToEdit}
          />
        </TabsContent>
        <TabsContent value="assignment" className="min-h-0 min-w-0 overflow-auto">
          <AssignmentTab value={editorState.actionModule} onChanged={updateModule} />
        </TabsContent>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t('editor.deleteConfirmTitle')}
        description={t('editor.deleteConfirmDescription')}
        confirmLabel={t('buttons.delete', { ns: 'common' })}
        confirmButtonVariant="destructive"
        isLoading={api.isDeleting}
        onConfirm={() => {
          setConfirmDelete(false)
          onDelete()
        }}
        onClose={() => setConfirmDelete(false)}
      />
      {(!isSuperAdmin || (isSuperAdmin && isTemplate)) && (
      <ConfirmDialog
        open={confirmCopy}
        title={isTemplate ? t('actionModuleTemplate.CopyFromTemplateConfirmTitle', { ns: 'action-modules' }) : t('actionModuleTemplate.CopyFromLiveConfirmTitle', { ns: 'action-modules' })}
        description={isTemplate ? t('actionModuleTemplate.CopyFromTemplateConfirmDescription', { ns: 'action-modules' }) : t('actionModuleTemplate.CopyFromLiveConfirmDescription', { ns: 'action-modules' })}
        confirmLabel={t('buttons.save', {ns: 'common'})}
        confirmButtonVariant="default"
        isLoading={api.isSaving}
        onConfirm={() => {
          setConfirmCopy(false)
          onCopy(false)
        }}
        onClose={() => setConfirmCopy(false)}
      />
      )}
      {/* Super-admin only: when copying a live action module to a template, let the user pick
          whether the new template should be a Global Template or a Company Template. */}
      {isSuperAdmin && !isTemplate && (
      <Dialog open={confirmSuperAdminCopy} onOpenChange={(o) => !o && setConfirmSuperAdminCopy(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('actionModuleTemplate.CopyFromLiveConfirmTitle', { ns: 'action-modules' })}</DialogTitle>
            <DialogDescription>
              {t('actionModuleTemplate.CopyFromLiveSuperAdminConfirmDescription', { ns: 'action-modules' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={api.isSaving} onClick={() => setConfirmSuperAdminCopy(false)}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button variant="default" disabled={api.isSaving} onClick={() => {
              setConfirmSuperAdminCopy(false)
              onCopy(false)
            }}>
              {t('actionModuleKind.options.CompanyTemplate', { ns: 'action-modules' })}
            </Button>
            <Button variant="default" disabled={api.isSaving} onClick={() => {
              setConfirmSuperAdminCopy(false)
              onCopy(true)
            }}>
              {t('actionModuleKind.options.GlobalTemplate', { ns: 'action-modules' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
      {copyModule && props.actionModuleId != null && (
        <CopyDialog
          actionModuleId={props.actionModuleId}
          onClose={() => setCopyModule(false)}
          onCopied={props.onCopied}
        />
      )}
    </Tabs>
  )
}
